import type { ReactNode } from "react";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { Logo } from "@/shared/components/ui/Logo";

export interface ErrorPageProps {
  title?: string;
  description?: ReactNode;
  /** When provided, renders a primary "Try again" action. */
  onTryAgain?: () => void;
  /** When provided, renders a secondary "Go back" action. */
  onGoBack?: () => void;
}

/**
 * Full-page error state shared by the route error boundary and the top-level
 * Sentry boundary. Presentational and static (CSS only) — a simple fade is well
 * within CSS, so it stays the on-spec default rather than reaching for Framer
 * Motion. Built from the sanctioned Section/Container shell, Logo, EmptyState,
 * and Button primitives.
 */
export function ErrorPage({
  title = "Something went wrong",
  description = "An unexpected error interrupted this page. You can try again, or head back to where you were.",
  onTryAgain,
  onGoBack,
}: ErrorPageProps) {
  const hasActions = Boolean(onTryAgain || onGoBack);

  return (
    <Section className="flex min-h-screen items-center justify-center">
      <Container size="2xl">
        <div className="glass-surface-strong flex flex-col items-center gap-8 rounded-2xl p-8">
          <Logo to="/app" />
          <EmptyState
            variant="page"
            title={title}
            description={description}
            action={
              hasActions ? (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {onTryAgain ? (
                    <Button variant="default" onClick={onTryAgain}>
                      Try again
                    </Button>
                  ) : null}
                  {onGoBack ? (
                    <Button variant="outline" onClick={onGoBack}>
                      Go back
                    </Button>
                  ) : null}
                </div>
              ) : undefined
            }
          />
        </div>
      </Container>
    </Section>
  );
}
